import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSelectionListChange } from '@angular/material/list';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, concat } from 'rxjs';
import { catchError, concatMap, debounceTime, distinctUntilChanged, flatMap, last, map, tap } from 'rxjs/operators';
import { DigestService, TenantService, UserService } from '../services';
import { GroupsService } from '../services/groups.service';

@Component({
    template: `
        <div fxLayout="column">
            <mat-toolbar>
                <h3>Add Members</h3>
                <span fxFlex></span>
                <button mat-raised-button color="primary" [disabled]="form?.invalid" (click)="save('ACTIVE')">Add User</button>
                <button mat-raised-button color="primary" [disabled]="form?.invalid" (click)="save('INVITED')" style="margin-left: 15px;">Add User and Send Invite</button>
            </mat-toolbar>
            <mat-card *ngIf="form" [formGroup]="form">
                <ng-container formArrayName="users">
                <ng-container *ngFor="let invitation of userGroups.controls; index as i; last as isLast" [formGroupName]="i">
                    <mat-card-content>
                        <div fxLayout="row">
                            <mat-form-field fxFlex="25">
                                <input matInput placeholder="EmailAddress" formControlName="emailAddress">
                                <mat-error *ngIf="invitation.get('emailAddress').hasError('required')">Email Address is required.</mat-error>
                                <mat-error *ngIf="invitation.get('emailAddress')?.hasError('pattern')">Email address must be valid.</mat-error>
                                <mat-error *ngIf="invitation.get('emailAddress')?.hasError('validateUniqueEmail')">Email address must be unique.</mat-error>
                            </mat-form-field>
                            <mat-form-field fxFlex="25" fxFlexOffset="15px">
                                <input matInput placeholder="First Name" formControlName="firstName">
                                <mat-error *ngIf="invitation.get('firstName').hasError('required')">First Name is required.</mat-error>
                            </mat-form-field>
                            <mat-form-field fxFlex="25" fxFlexOffset="15px">
                                <input matInput placeholder="Last Name" formControlName="lastName">
                                <mat-error *ngIf="invitation.get('lastName').hasError('required')">Last Name is required.</mat-error>
                            </mat-form-field>
                        </div>
                        <div fxLayout="row">
                            <mat-form-field fxFlex="65">
                                <textarea matInput cdkTextareaAutosize placeholder="Invitation Message" formControlName="inviteMessage"></textarea>
                            </mat-form-field>
                        </div>
                        <div fxLayout="row">
                            <mat-checkbox formControlName="activateDigest">Personal Briefing Active</mat-checkbox>
                        </div>
                        <button *ngIf="userGroups.controls.length !== 1" 
                                mat-icon-button
                                (click)="removeUserGroup(i)"
                                style="padding-top: 25px;"><mat-icon>close</mat-icon></button>
                    </mat-card-content>
                    <mat-card-actions *ngIf="isLast">
                        <button mat-button (click)="addUserGroup()" [disabled]="form.invalid">Add Another</button>
                    </mat-card-actions>
                </ng-container>
                </ng-container>
            </mat-card>
            <mat-card *ngIf="groups" >
                <mat-card-content>
                    <mat-selection-list (selectionChange)="selectionChanged($event)">
                        <div mat-header>User Groups</div>
                        <mat-list-option *ngFor="let group of groups" [value]="group">{{group.name}}</mat-list-option>
                    </mat-selection-list>
                </mat-card-content>
            </mat-card>
        </div>
    `
})
export class UserCreateComponent implements OnInit {
    form: FormGroup;
    timeZone: any;
    hostname: string;
    groups: any[];
    selectedGroups: any[] = [];
    noInitialDigest: boolean;

    constructor(private fb: FormBuilder,
                public snackBar: MatSnackBar,
                private route: ActivatedRoute,
                private router: Router, 
                private digestService: DigestService,
                private groupsService: GroupsService, 
                private tenantService: TenantService,
                private userService: UserService) {}

    ngOnInit(): void {
        this.tenantService.getUnauthTenantInfo().subscribe(tenant => {
            this.noInitialDigest = tenant.settings.noInitialDigest === 'true';
            this.timeZone = tenant.timeZone || 'US/Pacific';
            this.hostname = tenant.hostName;  
            this.form = this.fb.group({
                users: this.fb.array([this.createUserGroup()])
            });
        });
        this.groupsService.fetchGroups().subscribe(groupResults => this.groups = groupResults.content);
    }
    
    createUserGroup(): FormGroup {
        return this.fb.group({
            emailAddress: this.fb.control('', 
                                          [Validators.required, Validators.pattern('[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,63}$')],
                                          this.uniqueEmail.bind(this)),
            firstName: this.fb.control('', Validators.required),
            lastName: this.fb.control('', Validators.required),
            inviteMessage: this.fb.control(''),
            activateDigest: this.fb.control(!this.noInitialDigest),
            status: this.fb.control('ACTIVE'),
            timeZone: this.fb.control(this.timeZone) 
        });
    }

    get userGroups (): FormArray {
        return <FormArray>this.form.get('users');
    }

    uniqueEmail(control: AbstractControl): Observable<{ [key: string]: any }> {
        return this.userService.getUserByEmail(control.value, this.hostname)
        .pipe(
            debounceTime(250),
            distinctUntilChanged(),
            map(() => ({ validateUniqueEmail: true })),
            catchError(() => of(undefined))
        );
    }

    addUserGroup(): void {
        this.userGroups.push(this.createUserGroup());
    }

    removeUserGroup(i: number): void {
        this.userGroups.removeAt(i);
    }

    selectionChanged(event: MatSelectionListChange): void {
        this.selectedGroups = event.source.options.toArray().filter(option => option.selected).map(option => option.value);
    }

    save(status): void {
        const users = this.form.controls.users.value;
        users.forEach(user => user.status = status);
        this.userService.addNewUsers(users)
        .pipe(
            concatMap(createdUsers => {
                if (this.selectedGroups.length) {
                    const obs = createdUsers.reduce((acc, user) => acc.concat(this.selectedGroups.map(group => this.groupsService.addUser(group.id, user.id))), []);
                    return concat(...obs);
                }
                
                createdUsers
                .filter((user, index) => users[index].activateDigest)
                .forEach((user) => this.digestService.getDigestConfigurationsForUser(user.id, true).subscribe());
                
                return of(undefined);
            }),
            last(),
            tap(() => {
                const subject = this.form.controls.users.value.length === 1 ? 'User' : 'Users';
                this.snackBar.open(`${subject} added.`, 'OK', { duration: 3000});
            })
        )
        .subscribe(() => this.router.navigate(['../'], { relativeTo: this.route }));
    }
}